import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Star } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import "../styles/reviews.css";

export function useReviewFeed({ page = 1, rating = 0, limit = 10 } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api.get("/reviews/public", { params: { page, rating, limit }, signal: controller.signal, timeout: 15000 })
      .then(({ data: result }) => { setData(result); setError(""); })
      .catch((err) => { if (!controller.signal.aborted) setError(formatApiError(err)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [page, rating, limit, refresh]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") setRefresh((value) => value + 1);
    }, 30000);
    return () => window.clearInterval(timer);
  }, []);
  return { data, error, loading, reload: () => setRefresh((value) => value + 1) };
}

export function ReviewStars({ rating }) {
  return <span className="review-stars" role="img" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((star) => <Star key={star} aria-hidden="true" className={star <= rating ? "filled" : ""} />)}
  </span>;
}

export function reviewDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function ReviewEntry({ review, children }) {
  return <article className="review-entry" data-testid="player-review">
    <div className="review-entry-top">
      <div className="review-author"><span className="review-avatar" aria-hidden="true">{(review.username || "P").charAt(0).toUpperCase()}</span>
        <div className="review-author-name"><strong>{review.username}</strong><time dateTime={review.created_at}>{reviewDate(review.created_at)}</time></div>
      </div>
      <ReviewStars rating={review.rating} />
    </div>
    <p className="review-body">{review.text}</p>
    {review.updated_at !== review.created_at && <span className="review-muted review-edited">Edited {reviewDate(review.updated_at)}</span>}
    {children}
  </article>;
}

export function ReviewPager({ page, hasMore, onPage, loading }) {
  if (page === 1 && !hasMore) return null;
  return <nav className="review-pager" aria-label="Review pages">
    <button className="review-icon" title="Previous page" aria-label="Previous page" disabled={page === 1 || loading} onClick={() => onPage(page - 1)}><ChevronLeft /></button>
    <span>Page {page}</span>
    <button className="review-icon" title="Next page" aria-label="Next page" disabled={!hasMore || loading} onClick={() => onPage(page + 1)}><ChevronRight /></button>
  </nav>;
}

export function HomeReviews() {
  const { data, error, loading } = useReviewFeed({ limit: 3 });
  return <section className="reviews-scope review-home" aria-labelledby="home-reviews-title">
    <div className="review-heading">
      <div><h2 id="home-reviews-title">Player reviews</h2>
        {data?.summary.count > 0 && !error && <p className="review-muted"><Star className="review-inline-star" /> {data.summary.average.toFixed(1)} / 5 from {data.summary.count} {data.summary.count === 1 ? "review" : "reviews"}</p>}
      </div>
      <Link to="/reviews" className="review-link">All reviews <ArrowRight size={18} /></Link>
    </div>
    {error ? <p className="review-muted">Reviews are temporarily unavailable.</p> : !data && loading ? <p className="review-muted">Loading reviews...</p> : data?.reviews.length ?
      <div className="review-home-list">{data.reviews.map((review) => <ReviewEntry key={review.id} review={review} />)}</div> :
      <p className="review-muted">No reviews yet. <Link to="/reviews" className="review-link">Write a review</Link></p>}
  </section>;
}
